import noop from 'lodash/noop';
import { safeSetInterval, safeSetTimeout } from './safeTimers';

type SafeWindow = Window &
  typeof globalThis & {
    ReactNativeWebView: unknown;
    flutter_inappwebview: unknown;
  };

// Native-webview bridges an app must be able to detect/reach in ANY eval mode,
// read-only bindings included: their presence is an environment signal ("am I inside
// the native app?"), not a capability to gate. Single source of truth so this context
// and the read-only window mirror (readOnlyContext/safeWindowMirror) can't drift apart.
export const ALWAYS_AVAILABLE_WINDOW_KEYS = ['ReactNativeWebView', 'flutter_inappwebview'] as const;

function getAlwaysAvailableWindowEntries(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  const safeWindow = window as unknown as SafeWindow;
  const entries: Record<string, unknown> = {};
  for (const key of ALWAYS_AVAILABLE_WINDOW_KEYS) {
    entries[key] = safeWindow[key];
  }
  return entries;
}

export const safeGlobalContext = {
  alert: noop,
  prompt: noop,
  confirm: noop,
  window:
    typeof window === 'undefined'
      ? {}
      : {
          location: window.location,
          open: (...args: Parameters<typeof window.open>) => window.open(...args),
          opener: window.opener as Window | null,
          addEventListener: (...args: Parameters<typeof window.addEventListener>) =>
            window.addEventListener(...args),
          removeEventListener: (...args: Parameters<typeof window.removeEventListener>) =>
            window.removeEventListener(...args),
          dispatchEvent: (...args: Parameters<typeof window.dispatchEvent>) =>
            window.dispatchEvent(...args),
          print: (...args: Parameters<typeof window.print>) => window.print(...args),
          getUnifyAppsSettings: () =>
            'unifyAppsSettings' in window ? window.unifyAppsSettings : undefined,
          postMessage: (...args: Parameters<typeof window.postMessage>) =>
            window.postMessage(...args),
          ...getAlwaysAvailableWindowEntries(),
          setTimeout: safeSetTimeout,
          setInterval: safeSetInterval,
        },
  //   Added more properties to the safeGlobalContext
  constructor: {
    constructor: noop,
  },
};

// todo: Check for exhaustiveness of safeGlobalContext
/*
const noopReturningFalse = () => false;
const noopReturningEmptyString = () => '';
const noopReturningEmptyObject = () => ({});
const noopReturningEmptyArray = () => [];

// Create safe versions of built-in objects with neutered constructors
const createSafeObject = () => {
  const obj = {};
  Object.defineProperty(obj, 'constructor', {
    value: Object.create(null),
    writable: false,
    configurable: false,
  });
  return obj;
};

const createSafeFunction = () => {
  const fn = () => {};
  Object.defineProperty(fn, 'constructor', {
    value: Object.create(null),
    writable: false,
    configurable: false,
  });
  return fn;
};

const safeGlobalContext = {
  // Dialog functions
  alert: createSafeFunction(),
  prompt: createSafeFunction(),
  confirm: createSafeFunction(),

  // Core global functions
  eval: createSafeFunction(),
  Function: createSafeFunction(),
  setTimeout: createSafeFunction(),
  setInterval: createSafeFunction(),
  clearTimeout: createSafeFunction(),
  clearInterval: createSafeFunction(),

  // Network
  fetch: createSafeFunction(),
  XMLHttpRequest: createSafeFunction(),

  // Storage
  localStorage: createSafeObject(),
  sessionStorage: createSafeObject(),

  // Document
  document: createSafeObject(),

  // Window
  window: createSafeObject(),

  // Self-reference for completeness
  self: null,
  globalThis: null,
};

// Replace with specific implementations
const safeLocalStorage = safeGlobalContext.localStorage;
Object.defineProperties(safeLocalStorage, {
  getItem: { value: noopReturningEmptyString },
  setItem: { value: noop },
  removeItem: { value: noop },
  clear: { value: noop },
  key: { value: noopReturningEmptyString },
  length: { value: 0 },
});

// Similarly define properties for document, window, etc.
const safeDocument = safeGlobalContext.document;
Object.defineProperties(safeDocument, {
  cookie: { value: '', writable: false },
  location: { value: { href: 'about:blank' }, writable: false },
  write: { value: noop },
  open: { value: noop },
  close: { value: noop },
  createElement: { value: noopReturningEmptyObject },
  getElementById: { value: noopReturningEmptyObject },
  getElementsByTagName: { value: noopReturningEmptyArray },
  querySelector: { value: noopReturningEmptyObject },
  querySelectorAll: { value: noopReturningEmptyArray },
});

Create circular references
safeGlobalContext.self = safeGlobalContext;
safeGlobalContext.globalThis = safeGlobalContext;
*/
